import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {parseAxiosPromise} from '@/axiosInstance'
import {captureException} from '@sentry/react'
import {infiniteQueryFetchNextPage, ObjectEntries, raise} from '@/utilities/helpers'
import {httpGetUsers, HttpGetUsersOptions, HttpInfiniteUsersResponse} from '@/features/users/http/users.http'
import {z} from 'zod'
import {TableFilterKey} from '@/types'
import {useCallback} from 'react'

export const useGetInfiniteUsers = (
    promiseArgs: Omit<HttpGetUsersOptions['params'] & {filters?: Partial<Record<TableFilterKey, unknown>>}, 'page'>,
    options?: Pick<UseInfiniteQueryOptions<z.infer<typeof HttpInfiniteUsersResponse>>, 'enabled'>
) => {
    const normalizedFilters = useCallback((filters?: Partial<Record<TableFilterKey, unknown>>) => {
        if (!filters) return undefined
        const normalizedFiltersFields = ObjectEntries(filters).map(entry => {
            if (!entry) return raise('entry is required')

            const [key, value] = entry

            // add other exceptions here...
            if (key == 'specializationId') {
                return {
                    key,
                    operator: 'in' as const,
                    value: value
                }
            }

            // return default operator
            return {
                key,
                operator: '=' as const,
                value: value
            }
        })

        return {
            filters: {
                operator: 'and' as const,
                fields: normalizedFiltersFields
            }
        }
    }, [])

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.USERS, promiseArgs.search, promiseArgs],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetUsers({
                    params: {
                        page: pageParam,
                        search: promiseArgs.search,
                        limit: promiseArgs.limit
                    },
                    payload: normalizedFilters(promiseArgs.filters)
                }),
                responseShape: HttpInfiniteUsersResponse,
                onZodError: captureException
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : []
    }
}
